import {
	ExclamationCircleOutlined,
	QuestionCircleOutlined,
} from "@ant-design/icons";
import { Button, Divider, Form, Modal, Popconfirm, Switch } from "antd";
import { FormController, TFormStructure } from "formifex";
import { debounce } from "lodash";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FormBuilderSaveButton from "../components/Button/FormBuilderSaveButton";
import useCheckModule from "../hooks/useCheckModule";
import useFormHandler from "../hooks/useFormHandler";
import { getTenantSpecificUsers } from "../services/api-server/graphql";
import { updateTenantItem } from "../services/api-server/tenant_management";
import { useTenantStore } from "../stores/tenantStore";
import { tenantType } from "../types/tenant";
import { alertMessage } from "../utils/alert";
import { GetAntIcon } from "../utils/ant_icons";
import Emitter from "../utils/emitter";
import { HomeContext } from "./Home";

const DEBOUNCE_DELAY = 1000;

type ModuleConfigProps = {
	currentTenant: tenantType;
	setCurrentTenant: (tenant: tenantType) => void;
};

const ModuleConfig = ({
	currentTenant,
	setCurrentTenant,
}: ModuleConfigProps) => {
	// CONTEXT
	const { handleFormUpdating }: any = useContext(HomeContext);
	const { moduleList } = useCheckModule();
	const navigate = useNavigate();

	// STORE
	const tenant = useTenantStore((state) => state.tenant);
	const { setTenant } = useTenantStore();

	// FORM HANDLER
	const { formRef, resetForm } = useFormHandler(); // For switch on/off module (subscriptions)
	const form = FormController.useFormController(); // For module settings form (side panel)

	// MODAL
	const [modal, contextHolder] = Modal.useModal();

	// LOCAL STATE
	const [activeModule, setActiveModule] = useState<string | null>(null);
	const [pendingModules, setPendingModules] = useState<string[]>([]); // For instant UI update
	const [tempEnableReportingModule, setTempEnableReportingModule] =
		useState<boolean>(false);

	// Fetch lookup data for users and set initial values when current tenant is changed
	useEffect(() => {
		const fetchLookupData = async () => {
			const lookupData = {
				tenantUsersEmails: await getTenantSpecificUsers(currentTenant.uuid),
			};
			form.setLookupData(lookupData);
		};

		if (currentTenant) {
			const tenant_subscriptions = {
				subscriptions: currentTenant.subscriptions || [],
			};
			resetForm(tenant_subscriptions);
			setPendingModules(tenant_subscriptions.subscriptions);
			setTempEnableReportingModule(false);
			setActiveModule(null);
			fetchLookupData();
		}
	}, [currentTenant]);

	// Initialize form schema and initial form data when active module is changed
	useEffect(() => {
		if (activeModule) {
			const formSchema: TFormStructure = {
				id: "module_settings",
				layout: "vertical",
				requiredMark: "optional",
				items: [
					{
						id: "authorized_users_wrapper",
						type: "flex",
						className: "flex-col mb-4 gap-0!",
						children: [
							{
								label: "Authorised users",
								id: "authorized_users",
								type: "input",
								dataType: "string",
								inputType: "select",
								labelProp: "mail",
								valueProp: "mail",
								lookupKey: "tenantUsersEmails",
								properties: { showSearch: true, allowClear: true },
								required: false,
								mode: "multiple",
							},
							{
								id: "authorized_users_description",
								type: "custom",
								render: () => (
									<p className="text-gray-400">
										Select the users who are allowed to access this module.
										Anyone not on the list will not see the menu item in their
										view.
									</p>
								),
							},
						],
					},
				],
			};
			if (activeModule === "reporting" && formSchema.items) {
				formSchema.items.unshift({
					id: "report_type_wrapper",
					type: "flex",
					className: "flex-col mb-4 gap-0!",
					children: [
						{
							id: "enabled_reporting_types",
							label: "Report type",
							type: "input",
							dataType: "string",
							inputType: "select",
							options: [
								{
									label: "Audit",
									value: "audit",
								},
								{
									label: "Incident report (IR)",
									value: "incident_report",
								},
								{
									label: "Management of change (MoC)",
									value: "moc",
								},
								{
									label: "Opportunity for improvement (OFI)",
									value: "ofi",
								},
								{
									label: "Non-conformance report (NCR)",
									value: "non_conformance",
								},
							],
							required: true,
							mode: "multiple",
						},
						{
							id: "report_type_description",
							type: "custom",
							render: () => (
								<p className="text-gray-400">
									Select the report types available to this tenant. At least one
									report type must be selected.
								</p>
							),
						},
					],
				});
			}
			form.setSchema(formSchema);
			form.setInitialFormData({
				authorized_users:
					currentTenant?.modules_authorized_users?.[activeModule] || [],
				...(activeModule === "reporting" && {
					enabled_reporting_types: currentTenant?.enabled_reporting_types || [],
				}),
			});
		}
	}, [activeModule]);

	// Debounced save function
	const debouncedSave = useCallback(
		debounce(async (subscriptions: string[]) => {
			try {
				Emitter.emit("loading", {
					name: "module-config",
					isLoading: true,
				});
				const data = { subscriptions: subscriptions };
				const updatedTenant = await updateTenantItem(currentTenant.uuid, data);
				resetForm(updatedTenant);
				setPendingModules(subscriptions);
				setCurrentTenant(updatedTenant as tenantType);
				tenant?.role_key === currentTenant.role_key &&
					setTenant({ subscriptions: subscriptions });
			} catch (error) {
				console.error(error);
			} finally {
				Emitter.emit("loading", {
					name: "module-config",
					isLoading: false,
				});
			}
		}, DEBOUNCE_DELAY),
		[currentTenant]
	);

	const handleToggle = async (moduleKey: string, checked: boolean) => {
		const currentModules = pendingModules || [];

		let subscriptions;
		if (checked) {
			subscriptions = [...currentModules, moduleKey];
		} else {
			subscriptions = currentModules.filter((key: string) => key !== moduleKey);
		}

		if (
			moduleKey === "reporting" &&
			(!currentTenant.enabled_reporting_types ||
				currentTenant.enabled_reporting_types.length === 0)
		) {
			modal.confirm({
				content: (
					<>
						<p>
							To enable this module for the tenant, at least one report type
							must be selected.
						</p>
						<p>
							Select the required report type(s) in the module settings, then
							click <span className="font-bold">Save</span>
						</p>
					</>
				),
				icon: (
					<ExclamationCircleOutlined className="text-gray-400!" size={14} />
				),
				okText: "Ok, got it",
				cancelText: "Cancel",
				onOk: () => {
					setTempEnableReportingModule(true);
					resetForm({ subscriptions: subscriptions });
					setPendingModules(subscriptions);
					setActiveModule("reporting");
				},
			});
		} else {
			// Update form immediately for responsive UI
			resetForm({ subscriptions: subscriptions });
			setPendingModules(subscriptions);
			// Trigger debounced save
			debouncedSave(subscriptions);
		}
	};

	const handleModuleConfigSave = async () => {
		try {
			if (!activeModule) return;
			const data = {
				$set: {
					[`modules_authorized_users.${activeModule}`]:
						form.formData.authorized_users,
					...(activeModule === "reporting" && {
						enabled_reporting_types: form.formData.enabled_reporting_types,
					}),
					...(tempEnableReportingModule && {
						subscriptions: pendingModules,
					}),
				},
			};

			const updatedTenant = await updateTenantItem(currentTenant.uuid, data);
			setCurrentTenant(updatedTenant as tenantType);
			// Update tenant store
			if (tenant?.role_key === currentTenant.role_key) {
				setTenant({
					modules_authorized_users: (updatedTenant as tenantType)
						.modules_authorized_users,
					...(activeModule === "reporting" && {
						enabled_reporting_types: (updatedTenant as tenantType)
							.enabled_reporting_types,
					}),
					...(tempEnableReportingModule && {
						subscriptions: pendingModules,
					}),
				});
			}
			if (tempEnableReportingModule) {
				resetForm({ subscriptions: pendingModules });
				setTempEnableReportingModule(false);
			}
			form.setInitialFormData({
				authorized_users:
					(updatedTenant as tenantType)?.modules_authorized_users?.[
						activeModule
					] || [],
				...(activeModule === "reporting" && {
					enabled_reporting_types:
						(updatedTenant as tenantType)?.enabled_reporting_types || [],
				}),
			});
			alertMessage("success", "Module configuration saved successfully.");
		} catch (error) {
			console.error(error);
			alertMessage(
				"error",
				"Failed to save module configuration. Please try again."
			);
		}
	};

	const handleFormCancel = () => {
		handleFormUpdating(false, activeModule);
		setActiveModule(null);
		form.resetForm();
		if (tempEnableReportingModule) {
			const subscriptions = pendingModules.filter(
				(module: string) => module !== "reporting"
			);
			resetForm({ subscriptions: subscriptions });
			setPendingModules(subscriptions);
			setTempEnableReportingModule(false);
		}
	};

	return (
		<>
			{contextHolder}
			<div className="crud-tab-table-modal-container">
				<div className="main-container">
					<div className="generic-header">Module configuration</div>
					<div
						className="generic-content"
						style={{
							display: "flex",
							padding: "20px 30px",
							flexDirection: "column",
							alignItems: "flex-start",
							gap: "20px",
							flex: "1 1 0",
							alignSelf: "stretch",
							overflowY: "auto",
						}}
					>
						<Form form={formRef}>
							<Form.Item name="subscriptions" style={{ display: "none" }} />
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "flex-start",
									gap: "10px",
									alignSelf: "stretch",
									padding: "0px 0px",
								}}
							>
								{moduleList.map((module: any) => {
									const isChecked = (pendingModules || []).includes(module.key);

									return (
										<div
											key={module.key}
											style={{
												display: "flex",
												padding: "16px 24px",
												alignItems: "center",
												gap: "20px",
												alignSelf: "stretch",
												borderRadius: "3px",
												border: "1px solid grey",
											}}
										>
											<Switch
												disabled={
													module.key === "core" || tempEnableReportingModule
												}
												checked={isChecked}
												onChange={(checked) =>
													handleToggle(module.key, checked)
												}
											/>
											<Divider
												type="vertical"
												style={{
													height: "100%",
													margin: "0px",
													alignSelf: "stretch",
												}}
											/>
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													alignItems: "flex-start",
													gap: "4px",
													flex: "1 1 0",
												}}
											>
												<div>{module.name}</div>
												<div style={{ color: "grey" }}>
													{module.description}
												</div>
											</div>

											{module.key === "core" && (
												<Button
													type="link"
													icon={GetAntIcon("setting")}
													style={{ padding: "0px", alignSelf: "flex-start" }}
													onClick={() =>
														navigate(
															"/administration/tenant-configuration/module-roles",
															{
																state: {
																	tenantRoleKey: currentTenant.role_key,
																},
															}
														)
													}
												>
													Module settings
												</Button>
											)}

											{["reporting", "lessons-learned"].includes(
												module.key
											) && (
												<Button
													type="link"
													icon={GetAntIcon("setting")}
													style={{ padding: "0px", alignSelf: "flex-start" }}
													onClick={() => setActiveModule(module.key)}
													disabled={tempEnableReportingModule}
												>
													Module settings
												</Button>
											)}
										</div>
									);
								})}
							</div>
						</Form>
					</div>
				</div>
				<div
					className={`side-container transition-all duration-300 ease-in-out min-w-1/3 max-w-1/3 h-full  ${
						activeModule
							? "translate-x-0 opacity-100"
							: "translate-x-full opacity-0"
					}`}
				>
					<div
						className={`generic-header ${!activeModule ? "shadow-none!" : ""}`}
					>
						Module settings
					</div>
					<div className="w-full h-full p-4 overflow-y-auto">
						<div className="flex flex-col gap-4">
							{activeModule === "reporting" && (
								<div className="flex items-start gap-2 text-gray-400">
									<QuestionCircleOutlined style={{ marginTop: "4px" }} />
									<p className="text-wrap">
										This module can only be enabled if at least one report type
										is selected.
									</p>
								</div>
							)}
							<FormController
								formController={form}
								theme={"dark"}
								enablePostSave={true}
								resetOnDependencyChange={true}
								resetOnDependencyEmpty={true}
							/>
						</div>
					</div>
					<div className="generic-footer gap-2!">
						{form.isDirty ? (
							<Popconfirm
								icon={<ExclamationCircleOutlined style={{ color: "grey" }} />}
								title={
									<div style={{ maxWidth: "300px", padding: "10px" }}>
										<>
											You have unsaved changes. Discard them or continue
											editing?
										</>
									</div>
								}
								placement="topRight"
								cancelButtonProps={{ ghost: true }}
								cancelText="Discard changes"
								onCancel={handleFormCancel}
								okText="Continue editing"
								okType="default"
							>
								<Button className="ant-btn-secondary">Cancel</Button>
							</Popconfirm>
						) : (
							<Button className="ant-btn-secondary" onClick={handleFormCancel}>
								Cancel
							</Button>
						)}
						<FormBuilderSaveButton
							form={form}
							handleFormSave={handleModuleConfigSave}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export default ModuleConfig;
